import { SEO, pageSEO } from '../components/shared/SEO';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, BookOpen, Video, ArrowRight, Download, ExternalLink,
  Clock, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/shared/SectionHeader';
import { AnimatedSection, StaggerContainer, StaggerItem } from '../components/shared/AnimatedSection';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const whitepapers = [
  {
    title: 'Cloud Migration Best Practices for Guidewire',
    description: 'A comprehensive guide to planning and executing successful Guidewire Cloud migrations.',
    category: 'Cloud',
    readTime: '15 min',
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&h=250&fit=crop',
  },
  {
    title: 'Optimizing Claims Processing with Automation',
    description: 'How to leverage automation and AI to reduce claims cycle times and improve accuracy.',
    category: 'ClaimCenter',
    readTime: '12 min',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop',
  },
  {
    title: 'Building Scalable Rating Engines',
    description: 'Architecture patterns for high-performance rating in PolicyCenter implementations.',
    category: 'PolicyCenter',
    readTime: '10 min',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop',
  },
];

const blogs = [
  {
    title: 'The Future of Insurance Technology: 2025 Trends',
    description: 'Key trends shaping the insurance industry and how Guidewire enables digital transformation.',
    date: 'Dec 15, 2025',
    author: 'Sarah Chen',
    category: 'Industry Trends',
    image: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=400&h=250&fit=crop',
  },
  {
    title: 'Accelerating Guidewire Implementations with Agile',
    description: 'How agile methodologies can reduce implementation timelines and improve outcomes.',
    date: 'Dec 10, 2025',
    author: 'Michael Roberts',
    category: 'Best Practices',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=250&fit=crop',
  },
  {
    title: 'API-First Architecture for Modern Insurance',
    description: 'Building flexible, integration-ready insurance platforms with API-first design principles.',
    date: 'Dec 5, 2025',
    author: 'David Park',
    category: 'Technology',
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=250&fit=crop',
  },
  {
    title: 'Mastering Guidewire Data Migration',
    description: 'Proven strategies for successful data migration to Guidewire platforms.',
    date: 'Nov 28, 2025',
    author: 'Emily Johnson',
    category: 'Implementation',
    image: 'https://images.unsplash.com/photo-1544383835-bda2bc66a55d?w=400&h=250&fit=crop',
  },
];

const webinars = [
  {
    title: 'Guidewire Cloud: What You Need to Know',
    description: 'Join our cloud experts for an in-depth look at Guidewire Cloud Platform capabilities.',
    date: 'Jan 15, 2026',
    time: '2:00 PM EST',
    speakers: ['Sarah Chen', 'David Park'],
    status: 'upcoming',
  },
  {
    title: 'Automating Claims: Real-World Success Stories',
    description: 'Learn how leading insurers are using automation to transform claims operations.',
    date: 'Dec 20, 2025',
    time: '11:00 AM EST',
    speakers: ['Michael Roberts'],
    status: 'recording',
  },
  {
    title: 'Building High-Performance Rating Engines',
    description: 'Technical deep-dive into rating engine optimization for PolicyCenter.',
    date: 'Dec 5, 2025',
    time: '1:00 PM EST',
    speakers: ['David Park', 'Emily Johnson'],
    status: 'recording',
  },
];

const Resources = () => {
  const [activeTab, setActiveTab] = useState('whitepapers');

  return (
    <div data-testid="resources-page">
      <SEO {...pageSEO.resources} />
      <PageHeader
        label="Resources"
        title="Insights & Resources"
        description="Explore our collection of whitepapers, blog posts, and webinars to stay ahead in insurance technology."
      />

      {/* Resource Tabs */}
      <section className="py-20 lg:py-32 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="whitepapers" className="w-full" onValueChange={setActiveTab}>
            <TabsList className="w-full max-w-lg mx-auto grid grid-cols-3 mb-12">
              <TabsTrigger value="whitepapers" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Whitepapers
              </TabsTrigger>
              <TabsTrigger value="blog" className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Blog
              </TabsTrigger>
              <TabsTrigger value="webinars" className="flex items-center gap-2">
                <Video className="w-4 h-4" />
                Webinars
              </TabsTrigger>
            </TabsList>

            {/* Whitepapers */}
            <TabsContent value="whitepapers">
              <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {whitepapers.map((paper, index) => (
                  <StaggerItem key={index}>
                    <motion.div
                      whileHover={{ y: -8 }}
                      className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm hover:shadow-xl overflow-hidden transition-shadow duration-300"
                      data-testid={`whitepaper-${index}`}
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img 
                          src={paper.image} 
                          alt={paper.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-sky-500 text-white text-xs font-semibold rounded-full">
                            {paper.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-6">
                        <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 line-clamp-2">
                          {paper.title}
                        </h3>
                        <p className="text-slate-600 dark:text-slate-300 text-sm mb-4 line-clamp-2">
                          {paper.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                            <Clock className="w-3 h-3" />
                            {paper.readTime} read
                          </span>
                          <Button variant="ghost" size="sm" className="text-sky-600 hover:text-sky-700">
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  </StaggerItem>
                ))}
              </StaggerContainer>
            </TabsContent>

            {/* Blog */}
            <TabsContent value="blog">
              <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {blogs.map((post, index) => (
                  <StaggerItem key={index}>
                    <motion.div
                      whileHover={{ y: -8 }}
                      className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm hover:shadow-xl overflow-hidden transition-shadow duration-300 flex flex-col md:flex-row"
                      data-testid={`blog-${index}`}
                    >
                      <div className="relative w-full md:w-48 h-48 md:h-auto flex-shrink-0 overflow-hidden">
                        <img 
                          src={post.image} 
                          alt={post.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-6 flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-sky-600 font-semibold">
                            {post.category}
                          </span>
                          <span className="text-slate-300 dark:text-slate-600">•</span>
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            {post.date}
                          </span>
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 line-clamp-2">
                          {post.title}
                        </h3>
                        <p className="text-slate-600 dark:text-slate-300 text-sm mb-4 line-clamp-2">
                          {post.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            By {post.author}
                          </span>
                          <Button variant="ghost" size="sm" className="text-sky-600 hover:text-sky-700">
                            Read More
                            <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  </StaggerItem>
                ))}
              </StaggerContainer>
            </TabsContent>

            {/* Webinars */}
            <TabsContent value="webinars">
              <StaggerContainer className="space-y-6">
                {webinars.map((webinar, index) => (
                  <StaggerItem key={index}>
                    <motion.div
                      whileHover={{ x: 4 }}
                      className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm hover:shadow-lg p-6 transition-shadow duration-300"
                      data-testid={`webinar-${index}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {webinar.status === 'upcoming' ? (
                              <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 text-xs font-semibold rounded-full">
                                Upcoming
                              </span>
                            ) : (
                              <span className="px-3 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-semibold rounded-full">
                                Recording Available
                              </span>
                            )}
                            <span className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                              <Calendar className="w-3 h-3" />
                              {webinar.date} at {webinar.time}
                            </span>
                          </div>
                          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                            {webinar.title}
                          </h3>
                          <p className="text-slate-600 dark:text-slate-300 text-sm mb-3">
                            {webinar.description}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Speakers: {webinar.speakers.join(', ')}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          {webinar.status === 'upcoming' ? (
                            <Button className="bg-sky-600 hover:bg-sky-700 text-white">
                              Register Now
                              <ExternalLink className="w-4 h-4 ml-2" />
                            </Button>
                          ) : (
                            <Button variant="outline" className="border-sky-200 dark:border-slate-600 text-sky-600 dark:text-sky-400">
                              Watch Recording
                              <Video className="w-4 h-4 ml-2" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  </StaggerItem>
                ))}
              </StaggerContainer>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-sky-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
              Stay Updated
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Subscribe to our newsletter for the latest insights on Guidewire 
              and insurance technology trends.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <input
                type="email"
                placeholder="Enter your email"
                className="px-6 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none w-full sm:w-80"
                data-testid="newsletter-email-input"
              />
              <Button 
                className="bg-sky-600 hover:bg-sky-700 text-white px-8"
                data-testid="newsletter-subscribe-btn"
              >
                Subscribe
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Resources;
